import { ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ArrowLeft2 } from '@/components/Icons.tsx';
import { TypoCallout, TypoCaption } from '@/components/Typography.tsx';
import LADDER_QUESTION_VAULT from '@/graphql/queries/ladder-question-vault.graphql';

import {
  VaultCategory, VaultGroup, POOL_LABELS, TONE_TEXT, formatDate, formatPercent, getReviewPath,
} from './common.ts';
import { Badge, EmptyState, InlineLoader, Panel, ReviewLink, Table } from './components.tsx';

const useVault = () => {
  const { data, loading } = useQuery(LADDER_QUESTION_VAULT);
  const groups: VaultGroup[] = (data?.ladderQuestionVault?.groups ?? [])
    // The synthetic OTHER group only matters when something landed in it.
    .filter((group: VaultGroup) => group.studycenterGroupId !== null || group.answered > 0);

  return { loading: loading && !data, groups, vault: data?.ladderQuestionVault };
};

const LadderVault = () => {
  const { loading, groups, vault } = useVault();
  const [searchParams, setSearchParams] = useSearchParams();
  const groupName = searchParams.get('group');
  const visible = groupName ? groups.filter(group => group.name === groupName) : groups;
  const typeCount = groups.reduce((sum, group) => sum + group.categories.length, 0);

  if (loading) return <InlineLoader />;

  return (
    <Panel
      title={`Question Vault (${typeCount} question types)`}
      action={vault && (
        <Badge tone="info">{vault.answered} answered · {formatPercent(vault.correct, vault.answered)} correct</Badge>
      )}
    >
      <TypoCallout className="block mb-4 text-neutral2-text-soft">
        Every question you’ve answered in the app, filed by question type. Open a folder to review them.
      </TypoCallout>
      <div className="flex flex-wrap gap-2 mb-6">
        <FilterButton active={!groupName} onClick={() => setSearchParams({})}>All ({typeCount})</FilterButton>
        {groups.map(group => (
          <FilterButton
            key={group.name}
            active={groupName === group.name}
            onClick={() => setSearchParams({ group: group.name })}
          >
            {group.name} ({group.categories.length})
          </FilterButton>
        ))}
      </div>
      {visible.map(group => (
        <div key={group.name} className="mb-6 last:mb-0">
          {!groupName && (
            <TypoCallout className="block mb-3 font-semibold text-neutral2-text-strong">
              {group.name}
              <span className="ml-2 font-normal text-neutral2-text-softer">
                {formatPercent(group.correct, group.answered)} accuracy · {group.answered} answered
              </span>
            </TypoCallout>
          )}
          <div className="grid grid-cols-2 cq-md:grid-cols-3 cq-lg:grid-cols-4 cq-xl:grid-cols-6 gap-3">
            {group.categories.map(category => (
              <CategoryTile key={category.questionCategoryId} category={category} />
            ))}
          </div>
        </div>
      ))}
    </Panel>
  );
};

const FilterButton = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 rounded-sm text-footnote font-semibold
      ${active ? 'bg-neutral2-canvas2 text-neutral2-canvas2-text' : 'bg-shade-5 hover:bg-shade-10'}`}
  >
    {children}
  </button>
);

const CategoryTile = ({ category }: { category: VaultCategory }) => {
  const content = (
    <>
      <TypoCallout className="font-semibold leading-tight text-neutral-text-strong">{category.name}</TypoCallout>
      {category.answered ? (
        <div className="flex items-end justify-between gap-2">
          <TypoCaption className="text-neutral-text-softer">{category.answered} answered</TypoCaption>
          <strong className={`text-callout ${TONE_TEXT[getAccuracyTone(category)]}`}>
            {formatPercent(category.correct, category.answered)}
          </strong>
        </div>
      ) : (
        <TypoCaption className="text-neutral-text-weak">Nothing answered yet</TypoCaption>
      )}
    </>
  );
  const className = 'flex flex-col justify-between gap-3 min-h-[6.5rem] p-3 rounded-lg bg-neutral';

  // Untouched types stay visible so the folder counts match the Study Center, but there's nothing to open.
  if (!category.answered) return <div className={`${className} opacity-50`}>{content}</div>;

  return (
    <Link to={`/ladder/vault/${category.questionCategoryId}`} className={`${className} hover:bg-neutral-hover`}>
      {content}
    </Link>
  );
};

const getAccuracyTone = ({ correct, answered }: VaultCategory) => {
  const accuracy = answered ? correct / answered : 0;
  if (accuracy >= 0.8) return 'success';
  if (accuracy >= 0.6) return 'warning';
  return 'danger';
};

export const LadderVaultCategory = () => {
  const { categoryId } = useParams();
  const { loading, groups } = useVault();
  const group = groups.find(g => g.categories.some(c => c.questionCategoryId === Number(categoryId)));
  const category = group?.categories.find(c => c.questionCategoryId === Number(categoryId));

  if (loading) return <InlineLoader />;

  return (
    <Panel>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Link
          to={group ? `/ladder/vault?group=${encodeURIComponent(group.name)}` : '/ladder/vault'}
          className="flex items-center rounded px-4 py-2 gap-x-2 bg-shade-5 hover:bg-shade-10 whitespace-nowrap"
        >
          <ArrowLeft2 width={16} height={16} /><span className="text-common-white text-callout">Back</span>
        </Link>
        <div>
          <TypoCallout className="block font-semibold text-neutral2-text-strong">
            {category?.name ?? 'Unknown question type'}
          </TypoCallout>
          {category && (
            <TypoCaption className="text-neutral2-text-softer">
              {group?.name} · {category.answered} answered
              {' · '}{formatPercent(category.correct, category.answered)} correct
            </TypoCaption>
          )}
        </div>
      </div>
      {!category?.items.length ? <EmptyState text="No questions in this folder yet." /> : (
        <Table
          head={[
            { label: 'Question' },
            { label: 'Mode' },
            { label: 'Result', className: 'text-center' },
            { label: 'Date' },
            { label: '', className: 'text-right' },
          ]}
        >
          {category.items.map(item => (
            <tr key={item.clientExamQuestionId} className="hover:bg-shade-5">
              <td className="px-3 py-3 font-semibold text-neutral2-text-strong">{item.label}</td>
              <td className="px-3 py-3 whitespace-nowrap text-neutral2-text-softer">{POOL_LABELS[item.pool]}</td>
              <td className="px-3 py-3 text-center">
                <Badge tone={item.correct ? 'success' : 'danger'}>
                  {item.correct ? 'Correct ✓' : 'Incorrect ✗'}
                </Badge>
              </td>
              <td className="px-3 py-3 whitespace-nowrap text-neutral2-text-softer">{formatDate(item.playedAt)}</td>
              <td className="px-3 py-3 text-right"><ReviewLink to={getReviewPath(item)} /></td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
};

export default LadderVault;
